import { useCallback, useEffect, useRef, useState } from 'react';
import {
  shouldHoldUpdatesForFormFill,
  subscribeFormFill,
} from '../lib/forms/formFillGuard';
import { applyUpdate } from './applyUpdate';
import { recordUpdateEvent } from './updateTelemetry';
import { isForced, isNewer, isValidVersionInfo } from './versionCompare';
import type { AppVersionCheckState, VersionInfo } from './types';

const DEFAULT_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes
const THROTTLE_FLOOR_MS = 60 * 1000; // never hit the network more than once/min

export interface UseAppVersionCheckOptions {
  /** The build the running client booted with (baked in). */
  current: VersionInfo;
  /** Where the published version.json lives. */
  url?: string;
  /** Poll interval; defaults to 5 min. */
  intervalMs?: number;
  /** Changes to this value (e.g. route pathname) trigger a re-check. */
  routeKey?: string;
}

/**
 * Fetches the published version.json (cache-busted) and compares it to the baked
 * `current`. Offline-safe: skips when offline, swallows all errors, never blocks
 * the app. Polls on mount, on an interval, on window focus, and on route change.
 */
export function useAppVersionCheck(
  options: UseAppVersionCheckOptions,
): AppVersionCheckState {
  const { current, url = '/version.json', intervalMs = DEFAULT_INTERVAL_MS, routeKey } =
    options;

  const [latest, setLatest] = useState<VersionInfo | null>(null);
  const [checking, setChecking] = useState(false);
  const [lastCheckedAt, setLastCheckedAt] = useState<number | null>(null);

  // Refs so the stable check callback never goes stale or re-subscribes listeners.
  const lastAttemptRef = useRef(0);
  const inFlightRef = useRef(false);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  /**
   * `force` bypasses the throttle; `explicit` marks a user-requested check
   * (checkNow). Automatic checks — mount, interval, focus, online, route
   * change — stand down while a form fill holds updates (#4741): the
   * forced-update modal is full-screen and non-dismissible, so raising it
   * mid-FSR locks the technician out of the report. The hold is bounded by
   * FORM_FILL_MAX_HOLD_MS, and its release triggers a fresh check (below).
   */
  const runCheck = useCallback(
    async (force = false, explicit = false) => {
      if (!explicit && shouldHoldUpdatesForFormFill()) return;
      const now = Date.now();
      if (!force && now - lastAttemptRef.current < THROTTLE_FLOOR_MS) return;
      if (inFlightRef.current) return;
      if (typeof navigator !== 'undefined' && navigator.onLine === false) return;

      lastAttemptRef.current = now;
      inFlightRef.current = true;
      if (mountedRef.current) setChecking(true);
      try {
        const res = await fetch(`${url}?t=${now}`, {
          cache: 'no-store',
          credentials: 'omit',
        });
        if (!res.ok) return;
        const data: unknown = await res.json();
        if (!isValidVersionInfo(data)) return;
        if (!explicit && shouldHoldUpdatesForFormFill()) {
          // A fill opened while this request was in flight: drop the result
          // and clear the throttle so the first check after the fill ends
          // is not suppressed.
          lastAttemptRef.current = 0;
          return;
        }
        if (mountedRef.current) {
          setLatest(data);
          setLastCheckedAt(Date.now());
        }
      } catch {
        /* offline / transient / parse error — silent no-op, app never blocked */
      } finally {
        inFlightRef.current = false;
        if (mountedRef.current) setChecking(false);
      }
    },
    [url],
  );

  const checkNow = useCallback(() => {
    void runCheck(true, true);
  }, [runCheck]);

  // On mount + interval.
  useEffect(() => {
    void runCheck(true);
    const id = window.setInterval(() => void runCheck(false), intervalMs);
    return () => window.clearInterval(id);
  }, [runCheck, intervalMs]);

  // On window focus / regained connectivity.
  useEffect(() => {
    const onFocus = () => void runCheck(false);
    const onOnline = () => void runCheck(false);
    window.addEventListener('focus', onFocus);
    window.addEventListener('online', onOnline);
    return () => {
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('online', onOnline);
    };
  }, [runCheck]);

  // On the document becoming visible again (#4029). `focus` does not fire on
  // every platform's "return to a backgrounded tab/PWA" — an installed PWA
  // resumed from a phone's app-switcher can fire ONLY visibilitychange, which
  // previously left this check waiting on the next 5-min interval tick.
  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') void runCheck(false);
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [runCheck]);

  // When a form-fill hold RELEASES (the last fill closed, or the bounded hold
  // expired), re-check right away instead of waiting for the next interval,
  // focus or route event (#4741).
  useEffect(() => {
    let wasHeld = shouldHoldUpdatesForFormFill();
    return subscribeFormFill(() => {
      const held = shouldHoldUpdatesForFormFill();
      if (wasHeld && !held) void runCheck(true);
      wasHeld = held;
    });
  }, [runCheck]);

  // On route change.
  useEffect(() => {
    void runCheck(false);
  }, [routeKey, runCheck]);

  const updateAvailable = !!latest && isNewer(latest, current);
  const forceUpdate = updateAvailable && !!latest && isForced(latest, current);

  // Breadcrumb the DETECTION itself, once per newly-discovered target build.
  // Keyed on the target sha rather than on the boolean so a second deploy while
  // the tab is open is recorded as a second detection, not swallowed as "still
  // true". Detection is the one transition that happens even if the user never
  // sees, reads or touches the banner.
  const detectedShaRef = useRef<string | null>(null);
  useEffect(() => {
    if (!updateAvailable || !latest) return;
    if (detectedShaRef.current === latest.sha) return;
    detectedShaRef.current = latest.sha;
    recordUpdateEvent('update-detected', current, latest);
  }, [updateAvailable, latest, current]);

  return {
    current,
    latest,
    updateAvailable,
    forceUpdate,
    checking,
    lastCheckedAt,
    checkNow,
    applyUpdate,
  };
}
